import React from "react";
import Header from "../components/Header";

export default function SiteManagementPage() {
  return (
    <>
    <Header />
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 text-[#003366] font-sans p-4 md:p-6">
      {/* Enhanced Header */}
      <div className="flex items-center gap-4 md:gap-6 mb-8">
        <div className="relative">
          <div className="w-12 h-20 bg-gradient-to-b from-blue-500 to-blue-800 rounded-xl shadow-lg"></div>
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-red-500 rounded-full border-4 border-white"></div>
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent">
            7-Site Management Platform
          </h1>
          <p className="text-sm md:text-base text-blue-700 mt-2">AI-Powered Construction Site Intelligence & Safety System</p>
        </div>
      </div>

      {/* Enhanced System Overview */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
        <div className="flex items-center gap-3 mb-6">
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
          <h2 className="text-2xl font-bold">Intelligent Site Management System</h2>
        </div>
        <p className="text-base md:text-lg leading-relaxed">
          Implementing AI-driven identification of safety/quality risks & personnel misconduct, with closed-loop improvement workflows. Providing unified management of on-site construction personnel through biometric systems, enabling automated attendance tracking, work hour calculations, and comprehensive project data visualization for real-time decision making.
        </p>
        <div className="flex flex-wrap gap-3 mt-6">
          <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">AI Risk Detection</span>
          <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Biometric Access</span>
          <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Real-time Analytics</span>
          <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">IoT Integration</span>
        </div>
      </div>

      {/* Featured Functions Header */}
      <div className="text-center mb-10">
        <div className="inline-block relative">
          <h2 className="text-3xl font-bold text-red-700 mb-2">Featured Functions</h2>
          <div className="w-24 h-1 bg-red-700 mx-auto rounded-full"></div>
          <div className="absolute -top-2 -left-4 w-4 h-4 bg-red-200 rounded-full"></div>
          <div className="absolute -bottom-2 -right-4 w-4 h-4 bg-red-200 rounded-full"></div>
        </div>
        <p className="text-gray-600 mt-3">AI-powered tools for comprehensive site management and safety</p>
      </div>

      {/* Enhanced Functions Grid */}
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            {
              img: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
              title: "Safety & Quality Inspection",
              desc: "Automated inspections with AI-powered defect detection",
              color: "from-blue-600 to-blue-800"
            },
            {
              img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
              title: "Real-time Alert System",
              desc: "Instant notifications for safety violations and incidents",
              color: "from-red-600 to-red-800"
            },
            {
              img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
              title: "Data Analytics Dashboard",
              desc: "Comprehensive data consolidation and analysis",
              color: "from-green-600 to-green-800"
            },
            {
              img: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
              title: "AI Video Monitoring",
              desc: "Computer vision for behavior and risk identification",
              color: "from-purple-600 to-purple-800"
            }
          ].map((func, index) => (
            <div key={index} className="group cursor-pointer">
              <div className="overflow-hidden rounded-xl mb-4">
                <img
                  src={func.img}
                  alt={func.title}
                  className="w-full h-48 object-cover rounded-xl transform transition-transform duration-300 group-hover:scale-110"
                />
              </div>
              <div className={`bg-gradient-to-r ${func.color} text-white p-4 rounded-xl shadow-md`}>
                <h3 className="font-bold text-lg mb-2">{func.title}</h3>
                <p className="text-sm opacity-90">{func.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Function Descriptions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl shadow-md border border-blue-100">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414l-.707.707V8a1 1 0 10-2 0v1.586l-.707-.707a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l2-2z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-bold text-gray-800">Automated Safety Checks</h4>
            <p className="text-sm text-gray-600 mt-2">Continuous monitoring with instant violation alerts</p>
          </div>
          
          <div className="bg-gradient-to-br from-red-50 to-white p-5 rounded-xl shadow-md border border-red-100">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" />
              </svg>
            </div>
            <h4 className="font-bold text-gray-800">Real-time Alerts</h4>
            <p className="text-sm text-gray-600 mt-2">Immediate notifications for critical incidents</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-white p-5 rounded-xl shadow-md border border-green-100">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-bold text-gray-800">Data Intelligence</h4>
            <p className="text-sm text-gray-600 mt-2">Advanced analytics for predictive insights</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-white p-5 rounded-xl shadow-md border border-purple-100">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="font-bold text-gray-800">AI Surveillance</h4>
            <p className="text-sm text-gray-600 mt-2">24/7 monitoring with computer vision</p>
          </div>
        </div>
      </div>

      {/* Enhanced Application Value */}
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-10">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-blue-900 mb-2">Comprehensive Application Value</h2>
          <p className="text-gray-600">End-to-end site management solutions for maximum efficiency and safety</p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: "Smart Dashboard", icon: "📊", color: "bg-blue-100 text-blue-700" },
            { label: "Real-time Monitoring", icon: "👁️", color: "bg-green-100 text-green-700" },
            { label: "Personnel Access", icon: "👤", color: "bg-purple-100 text-purple-700" },
            { label: "Vehicle Tracking", icon: "🚛", color: "bg-orange-100 text-orange-700" },
            { label: "Equipment Management", icon: "🔧", color: "bg-red-100 text-red-700" },
            { label: "Safety Supervision", icon: "🛡️", color: "bg-indigo-100 text-indigo-700" },
            { label: "Video Analytics", icon: "📹", color: "bg-pink-100 text-pink-700" },
            { label: "Risk Detection", icon: "⚠️", color: "bg-yellow-100 text-yellow-700" },
            { label: "Quality Control", icon: "✅", color: "bg-teal-100 text-teal-700" },
            { label: "Environmental Monitoring", icon: "🌡️", color: "bg-cyan-100 text-cyan-700" },
            { label: "Attendance Automation", icon: "⏰", color: "bg-gray-100 text-gray-700" },
            { label: "Work Hour Tracking", icon: "📈", color: "bg-lime-100 text-lime-700" }
          ].map((item, index) => (
            <div key={index} className="group cursor-pointer">
              <div className={`${item.color} p-4 rounded-xl shadow-sm text-center transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-md`}>
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="text-sm font-medium">{item.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Application Scenarios */}
      <div className="bg-gradient-to-r from-blue-50 to-gray-50 rounded-2xl shadow-xl p-6 md:p-8 border border-blue-100">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="md:w-2/3">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">End-to-End Project Lifecycle Management</h2>
            <p className="text-gray-700 mb-6">
              <span className="font-semibold">Applicable Scope:</span> All Engineering Project Sites<br />
              <span className="font-semibold">Management Phases:</span> Site Mobilization → Active Construction → Site Demobilization<br />
              <span className="font-semibold">Coverage:</span> Full lifecycle management with complete data traceability
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                <div className="text-xl font-bold text-blue-700">Phase 1</div>
                <div className="text-sm text-gray-600">Mobilization</div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm text-center border-2 border-blue-500">
                <div className="text-xl font-bold text-blue-700">Phase 2</div>
                <div className="text-sm text-gray-600">Active Construction</div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                <div className="text-xl font-bold text-blue-700">Phase 3</div>
                <div className="text-sm text-gray-600">Demobilization</div>
              </div>
            </div>
          </div>
          
          <div className="md:w-1/3">
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-2xl shadow-lg">
              <h3 className="font-bold text-lg mb-4">Key Metrics</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-2xl font-bold">85%</div>
                  <div className="text-sm opacity-90">Safety Incident Reduction</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">40%</div>
                  <div className="text-sm opacity-90">Operational Efficiency Gain</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">99%</div>
                  <div className="text-sm opacity-90">Regulatory Compliance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100">
          <div className="text-2xl font-bold text-blue-800">2000+</div>
          <div className="text-sm text-gray-600">Sites Managed</div>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100">
          <div className="text-2xl font-bold text-blue-800">50,000+</div>
          <div className="text-sm text-gray-600">Personnel Tracked</div>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100">
          <div className="text-2xl font-bold text-blue-800">24/7</div>
          <div className="text-sm text-gray-600">AI Monitoring</div>
        </div>
        <div className="bg-white p-5 rounded-xl shadow-md border border-blue-100">
          <div className="text-2xl font-bold text-blue-800">99.9%</div>
          <div className="text-sm text-gray-600">Uptime Reliability</div>
        </div>
      </div>
    </div>
    </>
  );
}